// Custom Markdown component overrides for product-specific link/image behavior
// and chat typography.
// Used by MarkdownText.tsx's StreamdownTextPrimitive instance.

import { useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { useAuiState, type ThreadAssistantMessagePart } from '@assistant-ui/react';
import { getToken } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import { DotMatrix } from '@/shared/ui/dot-matrix';
import { delegatedAgentId } from '@/shared/chat-runtime/toThreadMessages';
import { useSpecialistDialogStore } from '@/shared/stores/specialistDialog';
import { downloadBlob } from '../model/markdownExport';
import { isAllowedMarkdownHref } from './markdownSafety';

const ARTIFACT_DOWNLOAD_PATH_RE = /\/api\/v1\/artifacts\/[^/]+\/download\/?$/;
const DOWNLOAD_FILENAME_RE = /\.(?:csv|docx?|html?|jpe?g|json|md|pdf|png|pptx?|tsv|txt|css|ya?ml|xml|toml|odt|rtf|gif|webp|zip|tar|gz|xlsx?)$/i;
// The synthesized answer cites a specialist by agent_id (a stable slug the
// model already uses correctly in prose), never by tool_call_id -- asking the
// model to reproduce an opaque id verbatim is fragile. This resolves the
// agent_id back to that specialist's actual tool-call part client-side.
const SPECIALIST_CITATION_RE = /^#specialist-agent-(.+)$/;

const linkClassName = 'text-primary underline underline-offset-2 hover:no-underline';

function childrenText(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenText).join('');
  return '';
}

function displayDownloadLabel(label: string): string {
  return label.replace(/^download\s+/i, '').trim() || label;
}

function isInternalArtifactDownloadHref(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return ARTIFACT_DOWNLOAD_PATH_RE.test(url.pathname);
  } catch {
    return ARTIFACT_DOWNLOAD_PATH_RE.test(href);
  }
}

function isDownloadLink(href: string, label: string): boolean {
  return isInternalArtifactDownloadHref(href) || DOWNLOAD_FILENAME_RE.test(label.trim()) || DOWNLOAD_FILENAME_RE.test(href.split('?')[0] ?? href);
}

async function downloadInternalArtifact(href: string, filename: string): Promise<void> {
  const token = getToken();
  const response = await fetch(href, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) return;
  const blob = await response.blob();
  downloadBlob(blob, filename || 'download');
}

export function MarkdownLink({ href, children, className, ...rest }: ComponentPropsWithoutRef<'a'>) {
  const [downloading, setDownloading] = useState(false);
  const requestSpecialistDialog = useSpecialistDialogStore((state) => state.request);
  const messageContent = useAuiState((state) => state.message.content) as ThreadAssistantMessagePart[];
  if (!href || !isAllowedMarkdownHref(href)) return <span {...rest}>{children}</span>;

  const citationAgentId = SPECIALIST_CITATION_RE.exec(href)?.[1] ?? null;
  if (citationAgentId) {
    const specialistCards = messageContent.filter(
      (part): part is Extract<ThreadAssistantMessagePart, { type: 'tool-call' }> =>
        part.type === 'tool-call' && part.toolName.startsWith('Agent:') && delegatedAgentId(part.args) === citationAgentId,
    );
    // A specialist can appear as two cards per turn: a quick "bid" capability
    // check, then the real "assignment" doing the substantive work a citation
    // is actually about. Prefer the assignment card; fall back to the bid
    // card only if no assignment card exists yet (e.g. still dispatching).
    const specialistPart = specialistCards.find((part) => part.args?.phase !== 'bid') ?? specialistCards[0];
    return (
      <a
        href={href}
        className={cn(linkClassName, className)}
        onClick={(event) => {
          event.preventDefault();
          if (specialistPart) requestSpecialistDialog(specialistPart.toolCallId);
        }}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const label = childrenText(children);
  const displayLabel = displayDownloadLabel(label);
  const internalArtifactLink = isInternalArtifactDownloadHref(href);
  const downloadLink = isDownloadLink(href, label);
  if (downloadLink) {
    return (
      <a
        href={href}
        target={internalArtifactLink ? undefined : '_blank'}
        rel={internalArtifactLink ? undefined : 'noopener noreferrer'}
        className={cn('inline-flex max-w-full items-center gap-1.5 text-primary underline underline-offset-2 hover:no-underline', className)}
        onClick={(event) => {
          if (!internalArtifactLink) return;
          event.preventDefault();
          if (downloading) return;
          setDownloading(true);
          void downloadInternalArtifact(href, label).finally(() => setDownloading(false));
        }}
        {...rest}
      >
        <span className="truncate">{displayLabel}</span>
        {downloading ? <DotMatrix state="downloading" className="size-3.5" /> : <Download className="size-3.5 shrink-0" />}
      </a>
    );
  }
  const isExternal = /^https?:\/\//.test(href);
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cn(linkClassName, className)} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <a href={href} className={cn(linkClassName, className)} {...rest}>
      {children}
    </a>
  );
}

export function SafeImage({ src, alt, ...rest }: ComponentPropsWithoutRef<'img'>) {
  if (!src || !/^(?:https?:\/\/|blob:|data:image\/(?:png|jpeg|jpg|gif|webp|svg\+xml);base64,)/i.test(src)
    && !(/^(?:\.?\.\/|\/)[^/]/.test(src) && !src.startsWith('//'))) return null;
  return <img src={src} alt={alt ?? ''} loading="lazy" {...rest} />;
}

export function MarkdownBlockquote({ children, ...rest }: ComponentPropsWithoutRef<'blockquote'>) {
  return (
    <blockquote className="my-3 rounded-md border-l-4 border-border bg-muted/40 px-4 py-3 text-sm leading-6 text-muted-foreground italic" {...rest}>
      {children}
    </blockquote>
  );
}

// Typography scale + spacing rhythm for the rest of the markdown surface.
// Streamdown owns code blocks, Mermaid blocks, and tables so its built-in
// controls stay intact.
export function MarkdownH1({ children, ...rest }: ComponentPropsWithoutRef<'h1'>) {
  return (
    <h1 className="mt-5 mb-2 text-xl leading-8 font-semibold first:mt-0" {...rest}>
      {children}
    </h1>
  );
}

export function MarkdownH2({ children, ...rest }: ComponentPropsWithoutRef<'h2'>) {
  return (
    <h2 className="mt-5 mb-2 border-b border-border/70 pb-1.5 text-lg leading-7 font-semibold first:mt-0" {...rest}>
      {children}
    </h2>
  );
}

export function MarkdownH3({ children, ...rest }: ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 className="mt-4 mb-1.5 text-base leading-7 font-semibold first:mt-0" {...rest}>
      {children}
    </h3>
  );
}

export function MarkdownH4({ children, ...rest }: ComponentPropsWithoutRef<'h4'>) {
  return (
    <h4 className="mt-3 mb-1 text-sm leading-6 font-semibold first:mt-0" {...rest}>
      {children}
    </h4>
  );
}

export function MarkdownParagraph({ children, ...rest }: ComponentPropsWithoutRef<'p'>) {
  return (
    <p className="mb-3 text-sm leading-7 last:mb-0" {...rest}>
      {children}
    </p>
  );
}

export function MarkdownUl({ children, ...rest }: ComponentPropsWithoutRef<'ul'>) {
  return (
    <ul className="mb-3 ml-5 list-disc text-sm leading-7 last:mb-0 [&>li+li]:mt-1.5" {...rest}>
      {children}
    </ul>
  );
}

export function MarkdownOl({ children, ...rest }: ComponentPropsWithoutRef<'ol'>) {
  return (
    <ol className="mb-3 ml-5 list-decimal text-sm leading-7 last:mb-0 [&>li+li]:mt-1.5" {...rest}>
      {children}
    </ol>
  );
}

export function MarkdownLi({ children, ...rest }: ComponentPropsWithoutRef<'li'>) {
  return (
    <li className="pl-1 marker:text-muted-foreground" {...rest}>
      {children}
    </li>
  );
}

export function MarkdownHr(rest: ComponentPropsWithoutRef<'hr'>) {
  return <hr className="my-4 border-border" {...rest} />;
}

export function MarkdownStrong({ children, ...rest }: ComponentPropsWithoutRef<'strong'>) {
  return (
    <strong className="font-semibold text-foreground" {...rest}>
      {children}
    </strong>
  );
}

export function MarkdownEm({ children, ...rest }: ComponentPropsWithoutRef<'em'>) {
  return (
    <em className="italic" {...rest}>
      {children}
    </em>
  );
}

// No `code`/`pre` overrides here: StreamdownTextPrimitive's `@streamdown/code`
// plugin owns rendering of both inline and block code directly on a `code`
// element (no `<pre>` ancestor — block styling comes from streamdown's own
// shipped classes, see the `@source` directives in tailwind.css), and it
// forces `pre` to its own internal `PreOverride` regardless of any `pre`
// component passed in — a custom `code`/`pre` override here would shadow the
// plugin and silently lose real Shiki highlighting. The copy button that used
// to live in a hand-rolled `pre` wrapper is now StreamdownTextPrimitive's
// built-in controls (see MarkdownText.tsx).
// No table overrides either: Streamdown's table component supplies
// copy/download/fullscreen controls around its own `data-streamdown` wrapper.
